import styles from './Items.module.css';

import { memo, useState } from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import Pagination from '@mui/material/Pagination';

import AttachMoney from '@mui/icons-material/AttachMoney';
import Check from '@mui/icons-material/Check';
import Delete from '@mui/icons-material/Delete';
import Copy from '@mui/icons-material/FileCopy';

import { useTheme } from '@mui/material/styles';

export const ItemsFooter = memo(({ paginationInfo, myList, paying = false, userIsAdmin, onSetPage, onCopyItems, onRemoveItems, onVerifyItems, onSellerPayout, selectedItems, disableCopyRemove, currentPage }) => {
    const [ confirmDeleteOpen, setConfirmDeleteOpen ] = useState(false);
    const [ confirmPayoutOpen, setConfirmPayoutOpen ] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleOnSetPage = (event, value) => {
        onSetPage(value);
    }

    const handleSellerPayout = () => {
        // check for non-sold items
        const unsoldItems = selectedItems.filter(item => !item.sold);

        // if there are unsold items, show confirmation dialog
        if (unsoldItems.length > 0) {
            setConfirmPayoutOpen(true);
        } else {
            // no unsold items, proceed directly
            onSellerPayout();
        }
    };

    const handleConfirmPayout = () => {
        setConfirmPayoutOpen(false);
        onSellerPayout();
    };

    return (
        <>
            <div className={ styles.footer }>
                <div className={ styles.actions }>
                    { isMobile ? (
                        <>
                            <IconButton
                                color="primary"
                                onClick={ onCopyItems }
                                disabled={ selectedItems.length === 0 }
                                title="Copy selected items"
                                size='large'
                            >
                                <Copy />
                            </IconButton>

                            <IconButton
                                color="secondary"
                                onClick={ () => setConfirmDeleteOpen(true) }
                                disabled={ selectedItems.filter(item => item.vendor || !item.feePaid).length === 0 }
                                title="Remove selected items"
                                size='large'
                            >
                                <Delete />
                            </IconButton>

                            { userIsAdmin && !myList && (
                                <IconButton
                                    color="success"
                                    onClick={ onVerifyItems }
                                    disabled={ disableCopyRemove }
                                    title="Verify selected items"
                                >
                                    <Check />
                                </IconButton>
                            ) }
                        </>
                    ) : (
                        <>
                            <Button variant='contained' color='primary' onClick={ onCopyItems } disabled={ selectedItems.length === 0 } startIcon={ <Icon component={ Copy } /> }>Copy</Button>
                            <Button variant='contained' color='secondary' onClick={ () => setConfirmDeleteOpen(true) } disabled={ selectedItems.filter(item => item.vendor || !item.feePaid).length === 0 } startIcon={ <Icon component={ Delete } /> }>Remove</Button>

                            { userIsAdmin && !myList && <Divider orientation="vertical" flexItem sx={ { mx: 1 } } /> }

                            { userIsAdmin && !myList && <Button variant='contained' color='success' onClick={ onVerifyItems } disabled={ selectedItems.length === 0 } startIcon={ <AttachMoney /> }>Verify</Button> }

                            { userIsAdmin && !myList && <Divider orientation="vertical" flexItem sx={ { mx: 1 } } /> }

                            { userIsAdmin && !myList && <Button variant='contained' color='success' onClick={ handleSellerPayout } disabled={ selectedItems.length === 0 } startIcon={ <Icon component={ Check } /> } loading={ paying } loadingPosition='start'>Payout</Button> }
                        </>
                    ) }
                </div>

                { paginationInfo.totalPages > 1 && <div className={ styles.page }>
                    <Pagination
                        color="primary"
                        count={ paginationInfo.totalPages }
                        page={ currentPage }
                        onChange={ handleOnSetPage }
                    />
                </div> }
            </div>

            <Dialog open={ confirmDeleteOpen } onClose={ () => setConfirmDeleteOpen(false) }>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to permanently delete the selected items?
                </DialogContent>
                <DialogActions>
                    <Button onClick={ () => setConfirmDeleteOpen(false) }>Cancel</Button>
                    <Button onClick={ () => { onRemoveItems(); setConfirmDeleteOpen(false); } } color="error">Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={ confirmPayoutOpen } onClose={ () => setConfirmPayoutOpen(false) }>
                <DialogTitle>Confirm Payout</DialogTitle>

                <DialogContent>
                    You have { selectedItems.filter(item => !item.sold).length } item(s) that are not marked as sold. Are you sure you want to include these items in the payout?
                </DialogContent>

                <DialogActions>
                    <Button onClick={ () => setConfirmPayoutOpen(false) }>Cancel</Button>
                    <Button onClick={ handleConfirmPayout } color="primary">Proceed</Button>
                </DialogActions>
            </Dialog>
        </>
    );
});

ItemsFooter.displayName = 'Items Footer';